import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./AccountOnboarding.css";
import { buildApiUrl } from "../../lib/api";
import { formatApiError } from "../../lib/apiError";

const STEP_CONFIG = [
    { key: "contact", label: "Contact", number: "01" },
    { key: "identity", label: "Identity", number: "02" },
    { key: "financial", label: "Financial Profile", number: "03" },
    { key: "disclosures", label: "Disclosures", number: "04" },
    { key: "agreements", label: "Agreements", number: "05" },
    { key: "review", label: "Review + Submit", number: "06" },
];

const INITIAL_FORM = {
    contact: {
        email_address: "",
        phone_number: "",
        street_address_1: "",
        street_address_2: "",
        city: "",
        state: "",
        postal_code: "",
        country: "USA",
    },
    identity: {
        given_name: "",
        family_name: "",
        date_of_birth: "",
        country_of_citizenship: "USA",
        country_of_birth: "USA",
        party_type: "natural_person",
        tax_id_type: "USA_SSN",
        tax_id: "",
        country_of_tax_residence: "USA",
        funding_source: [],
    },
    financial: {
        annual_income_min: "",
        annual_income_max: "",
        liquid_net_worth_min: "",
        liquid_net_worth_max: "",
        total_net_worth_min: "",
        total_net_worth_max: "",
        liquidity_needs: "low",
        risk_tolerance: "moderate",
        investment_time_horizon: "long_term",
        investment_objective: [],
    },
    disclosures: {
        is_control_person: false,
        is_affiliated_exchange_or_finra: false,
        is_politically_exposed: false,
        immediate_family_exposed: false,
    },
    agreements: {
        customer_agreement: false,
        ip_address: "127.0.0.1",
    },
};

function mergeDraftIntoForm(draft) {
    const payload = draft?.payload_json || {};

    return {
        contact: {
            ...INITIAL_FORM.contact,
            ...(payload.contact || {}),
            street_address_1: payload.address?.street_address?.[0] || "",
            street_address_2: payload.address?.street_address?.[1] || "",
            city: payload.address?.city || "",
            state: payload.address?.state || "",
            postal_code: payload.address?.postal_code || "",
            country: payload.address?.country || "USA",
        },
        identity: {
            ...INITIAL_FORM.identity,
            ...(payload.identity || {}),
            tax_id_type: payload.tax_info?.tax_id_type || payload.identity?.tax_id_type || "USA_SSN",
            tax_id: payload.tax_info?.tax_id || payload.identity?.tax_id || "",
            country_of_tax_residence: payload.tax_info?.country_of_tax_residence || "USA",
            funding_source: payload.tax_info?.funding_source || payload.identity?.funding_source || [],
        },
        financial: {
            ...INITIAL_FORM.financial,
            ...(payload.income || {}),
            ...(payload.experience || {}),
        },
        disclosures: {
            ...INITIAL_FORM.disclosures,
            ...(payload.disclosures || {}),
        },
        agreements: {
            ...INITIAL_FORM.agreements,
            customer_agreement: Boolean(payload.agreements?.length),
            ip_address: payload.agreements?.[0]?.ip_address || "127.0.0.1",
        },
    };
}

function formatCurrencyLikeValue(value) {
    if (value === "" || value === null || typeof value === "undefined") {
        return "Not provided";
    }
    return String(value);
}

function AccountOnboarding({ user, onExit, onSubmitted }) {
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [draft, setDraft] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let isMounted = true;

        const loadDraft = async () => {
            setIsLoading(true);
            try {
                await axios.post(buildApiUrl("/onboarding/start"), {}, { withCredentials: true });
                const { data } = await axios.get(buildApiUrl("/onboarding"), {
                    withCredentials: true,
                });
                if (!isMounted) {
                    return;
                }
                setDraft(data?.draft || null);
                setFormData(mergeDraftIntoForm(data?.draft));
            } catch (error) {
                if (isMounted) {
                    setErrorMessage(
                        formatApiError(error, "We could not load your onboarding draft.")
                    );
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadDraft();

        return () => {
            isMounted = false;
        };
    }, []);

    const progressPercentage = ((activeStep + 1) / STEP_CONFIG.length) * 100;

    const reviewSections = useMemo(() => {
        return [
            {
                title: "Contact",
                lines: [
                    `Email: ${formData.contact.email_address || "Not provided"}`,
                    `Phone: ${formData.contact.phone_number || "Not provided"}`,
                    `Address: ${[
                        formData.contact.street_address_1,
                        formData.contact.street_address_2,
                        formData.contact.city,
                        formData.contact.state,
                        formData.contact.postal_code,
                        formData.contact.country,
                    ]
                        .filter(Boolean)
                        .join(", ") || "Not provided"}`,
                ],
            },
            {
                title: "Identity",
                lines: [
                    `Name: ${`${formData.identity.given_name} ${formData.identity.family_name}`.trim() || "Not provided"}`,
                    `DOB: ${formData.identity.date_of_birth || "Not provided"}`,
                    `Citizenship: ${formData.identity.country_of_citizenship || "Not provided"}`,
                    `Tax ID Type: ${formData.identity.tax_id_type || "Not provided"}`,
                    `Tax ID: ${formData.identity.tax_id || "Not provided"}`,
                ],
            },
            {
                title: "Financial Profile",
                lines: [
                    `Annual income min: ${formatCurrencyLikeValue(formData.financial.annual_income_min)}`,
                    `Annual income max: ${formatCurrencyLikeValue(formData.financial.annual_income_max)}`,
                    `Risk tolerance: ${formData.financial.risk_tolerance || "Not provided"}`,
                    `Time horizon: ${formData.financial.investment_time_horizon || "Not provided"}`,
                    `Objectives: ${formData.financial.investment_objective.join(", ") || "Not provided"}`,
                ],
            },
            {
                title: "Disclosures",
                lines: [
                    `Control person: ${formData.disclosures.is_control_person ? "Yes" : "No"}`,
                    `Affiliated with exchange or FINRA: ${formData.disclosures.is_affiliated_exchange_or_finra ? "Yes" : "No"}`,
                    `Politically exposed: ${formData.disclosures.is_politically_exposed ? "Yes" : "No"}`,
                    `Immediate family politically exposed: ${formData.disclosures.immediate_family_exposed ? "Yes" : "No"}`,
                ],
            },
            {
                title: "Agreements",
                lines: [
                    `Customer agreement: ${formData.agreements.customer_agreement ? "Accepted" : "Not accepted"}`,
                    `Signing IP address: ${formData.agreements.ip_address || "Not provided"}`,
                ],
            },
        ];
    }, [formData]);

    const updateSection = (section, field, value) => {
        setFormData((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value,
            },
        }));
    };

    const toggleSelection = (section, field, value) => {
        const current = formData[section][field];
        const exists = current.includes(value);
        updateSection(
            section,
            field,
            exists ? current.filter((item) => item !== value) : [...current, value]
        );
    };

    const saveCurrentStep = async () => {
        const currentStepKey = STEP_CONFIG[activeStep].key;
        setErrorMessage("");
        setIsSaving(true);

        try {
            if (currentStepKey === "contact") {
                await axios.patch(
                    buildApiUrl("/onboarding/contact"),
                    {
                        email_address: formData.contact.email_address,
                        phone_number: formData.contact.phone_number,
                    },
                    { withCredentials: true }
                );

                const { data } = await axios.patch(
                    buildApiUrl("/onboarding/address"),
                    {
                        street_address: [
                            formData.contact.street_address_1,
                            formData.contact.street_address_2,
                        ].filter(Boolean),
                        city: formData.contact.city,
                        state: formData.contact.state,
                        postal_code: formData.contact.postal_code,
                        country: formData.contact.country,
                    },
                    { withCredentials: true }
                );
                setDraft(data?.draft || null);
            } else if (currentStepKey === "identity") {
                await axios.patch(
                    buildApiUrl("/onboarding/identity"),
                    {
                        given_name: formData.identity.given_name,
                        family_name: formData.identity.family_name,
                        date_of_birth: formData.identity.date_of_birth,
                        country_of_citizenship: formData.identity.country_of_citizenship,
                        country_of_birth: formData.identity.country_of_birth,
                        party_type: formData.identity.party_type,
                    },
                    { withCredentials: true }
                );

                const { data } = await axios.patch(
                    buildApiUrl("/onboarding/tax-info"),
                    {
                        tax_id_type: formData.identity.tax_id_type,
                        tax_id: formData.identity.tax_id,
                        country_of_tax_residence: formData.identity.country_of_tax_residence,
                        funding_source: formData.identity.funding_source,
                    },
                    { withCredentials: true }
                );
                setDraft(data?.draft || null);
            } else if (currentStepKey === "financial") {
                await axios.patch(
                    buildApiUrl("/onboarding/income"),
                    {
                        annual_income_min: Number(formData.financial.annual_income_min),
                        annual_income_max: Number(formData.financial.annual_income_max),
                        liquid_net_worth_min: Number(formData.financial.liquid_net_worth_min),
                        liquid_net_worth_max: Number(formData.financial.liquid_net_worth_max),
                        total_net_worth_min: Number(formData.financial.total_net_worth_min),
                        total_net_worth_max: Number(formData.financial.total_net_worth_max),
                    },
                    { withCredentials: true }
                );

                const { data } = await axios.patch(
                    buildApiUrl("/onboarding/experience"),
                    {
                        liquidity_needs: formData.financial.liquidity_needs,
                        risk_tolerance: formData.financial.risk_tolerance,
                        investment_time_horizon: formData.financial.investment_time_horizon,
                        investment_objective: formData.financial.investment_objective,
                    },
                    { withCredentials: true }
                );
                setDraft(data?.draft || null);
            } else if (currentStepKey === "disclosures") {
                const { data } = await axios.patch(
                    buildApiUrl("/onboarding/disclosures"),
                    formData.disclosures,
                    { withCredentials: true }
                );
                setDraft(data?.draft || null);
            } else if (currentStepKey === "agreements") {
                const { data } = await axios.patch(
                    buildApiUrl("/onboarding/agreements"),
                    {
                        agreements: formData.agreements.customer_agreement
                            ? [
                                  {
                                      agreement: "customer_agreement",
                                      signed_at: new Date().toISOString(),
                                      ip_address: formData.agreements.ip_address,
                                  },
                              ]
                            : [],
                    },
                    { withCredentials: true }
                );
                setDraft(data?.draft || null);
            } else if (currentStepKey === "review") {
                const { data } = await axios.post(
                    buildApiUrl("/onboarding/submit"),
                    {},
                    { withCredentials: true }
                );
                onSubmitted(data?.user || user || null);
                return;
            }

            setActiveStep((prev) => Math.min(prev + 1, STEP_CONFIG.length - 1));
        } catch (error) {
            setErrorMessage(
                formatApiError(error, "We could not save this step. Please review the fields and try again.")
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <section className="onboarding-shell">
                <div className="onboarding-panel onboarding-loading">Loading onboarding...</div>
            </section>
        );
    }

    return (
        <section className="onboarding-shell">
            <div className="onboarding-panel">
                <button type="button" className="onboarding-close" onClick={onExit}>
                    ×
                </button>

                <div className="onboarding-topline">Brokerage onboarding</div>
                <div className="onboarding-header-row">
                    <div>
                        <h1>Account onboarding</h1>
                        <p>
                            Each step saves to your draft. Complete the required profile
                            data, then submit for review.
                        </p>
                    </div>
                    <div className="onboarding-step-badge">
                        Step {activeStep + 1} of {STEP_CONFIG.length}
                    </div>
                </div>

                <div className="onboarding-progress-track">
                    <div
                        className="onboarding-progress-fill"
                        style={{ width: `${progressPercentage}%` }}
                    />
                </div>

                <div className="onboarding-step-tabs">
                    {STEP_CONFIG.map((step, index) => (
                        <button
                            key={step.key}
                            type="button"
                            className={`onboarding-step-tab ${index === activeStep ? "active" : ""}`}
                            onClick={() => setActiveStep(index)}
                        >
                            <span>{step.number}</span>
                            {step.label}
                        </button>
                    ))}
                </div>

                <div className="onboarding-layout">
                    <div className="onboarding-main">
                        {activeStep === 0 ? (
                            <section className="onboarding-card">
                                <h2>Contact</h2>
                                <p>Primary contact and residential address.</p>
                                <div className="onboarding-grid two-up">
                                    <label>
                                        <span>Email address</span>
                                        <input
                                            value={formData.contact.email_address}
                                            onChange={(event) => updateSection("contact", "email_address", event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Phone number</span>
                                        <input
                                            value={formData.contact.phone_number}
                                            onChange={(event) => updateSection("contact", "phone_number", event.target.value)}
                                        />
                                    </label>
                                </div>
                                <label>
                                    <span>Street address</span>
                                    <input
                                        value={formData.contact.street_address_1}
                                        onChange={(event) => updateSection("contact", "street_address_1", event.target.value)}
                                    />
                                </label>
                                <label>
                                    <span>Address line 2</span>
                                    <input
                                        value={formData.contact.street_address_2}
                                        onChange={(event) => updateSection("contact", "street_address_2", event.target.value)}
                                    />
                                </label>
                                <div className="onboarding-grid two-up">
                                    <label>
                                        <span>City</span>
                                        <input
                                            value={formData.contact.city}
                                            onChange={(event) => updateSection("contact", "city", event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>State</span>
                                        <input
                                            value={formData.contact.state}
                                            onChange={(event) => updateSection("contact", "state", event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Postal code</span>
                                        <input
                                            value={formData.contact.postal_code}
                                            onChange={(event) => updateSection("contact", "postal_code", event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Country</span>
                                        <input
                                            value={formData.contact.country}
                                            onChange={(event) => updateSection("contact", "country", event.target.value)}
                                        />
                                    </label>
                                </div>
                            </section>
                        ) : null}

                        {activeStep === 1 ? (
                            <section className="onboarding-card">
                                <h2>Identity</h2>
                                <p>Identity and tax details for the account holder.</p>
                                <div className="onboarding-grid two-up">
                                    <label>
                                        <span>First name</span>
                                        <input
                                            value={formData.identity.given_name}
                                            onChange={(event) => updateSection("identity", "given_name", event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Last name</span>
                                        <input
                                            value={formData.identity.family_name}
                                            onChange={(event) => updateSection("identity", "family_name", event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Date of birth</span>
                                        <input
                                            type="date"
                                            value={formData.identity.date_of_birth}
                                            onChange={(event) => updateSection("identity", "date_of_birth", event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Country of citizenship</span>
                                        <input
                                            value={formData.identity.country_of_citizenship}
                                            onChange={(event) => updateSection("identity", "country_of_citizenship", event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Country of birth</span>
                                        <input
                                            value={formData.identity.country_of_birth}
                                            onChange={(event) => updateSection("identity", "country_of_birth", event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Party type</span>
                                        <select
                                            value={formData.identity.party_type}
                                            onChange={(event) => updateSection("identity", "party_type", event.target.value)}
                                        >
                                            <option value="natural_person">Natural person</option>
                                        </select>
                                    </label>
                                    <label>
                                        <span>Tax ID type</span>
                                        <select
                                            value={formData.identity.tax_id_type}
                                            onChange={(event) => updateSection("identity", "tax_id_type", event.target.value)}
                                        >
                                            <option value="USA_SSN">US SSN</option>
                                            <option value="USA_ITIN">US ITIN</option>
                                        </select>
                                    </label>
                                    <label>
                                        <span>Tax ID</span>
                                        <input
                                            value={formData.identity.tax_id}
                                            onChange={(event) => updateSection("identity", "tax_id", event.target.value)}
                                        />
                                    </label>
                                </div>
                                <label>
                                    <span>Country of tax residence</span>
                                    <input
                                        value={formData.identity.country_of_tax_residence}
                                        onChange={(event) => updateSection("identity", "country_of_tax_residence", event.target.value)}
                                    />
                                </label>
                                <div className="onboarding-choice-grid">
                                    {[
                                        { label: "Employment income", value: "employment_income" },
                                        { label: "Investments", value: "investments" },
                                        { label: "Savings", value: "savings" },
                                    ].map((item) => (
                                        <label key={item.value} className="onboarding-check-card">
                                            <input
                                                type="checkbox"
                                                checked={formData.identity.funding_source.includes(item.value)}
                                                onChange={() => toggleSelection("identity", "funding_source", item.value)}
                                            />
                                            <span>{item.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {activeStep === 2 ? (
                            <section className="onboarding-card">
                                <h2>Financial Profile</h2>
                                <p>Suitability, liquidity, and investment profile.</p>
                                <div className="onboarding-grid two-up">
                                    {[
                                        ["annual_income_min", "Annual income minimum"],
                                        ["annual_income_max", "Annual income maximum"],
                                        ["liquid_net_worth_min", "Liquid net worth minimum"],
                                        ["liquid_net_worth_max", "Liquid net worth maximum"],
                                        ["total_net_worth_min", "Total net worth minimum"],
                                        ["total_net_worth_max", "Total net worth maximum"],
                                    ].map(([field, label]) => (
                                        <label key={field}>
                                            <span>{label}</span>
                                            <input
                                                type="number"
                                                value={formData.financial[field]}
                                                onChange={(event) => updateSection("financial", field, event.target.value)}
                                            />
                                        </label>
                                    ))}
                                </div>
                                <div className="onboarding-grid two-up">
                                    <label>
                                        <span>Liquidity needs</span>
                                        <select
                                            value={formData.financial.liquidity_needs}
                                            onChange={(event) => updateSection("financial", "liquidity_needs", event.target.value)}
                                        >
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                        </select>
                                    </label>
                                    <label>
                                        <span>Risk tolerance</span>
                                        <select
                                            value={formData.financial.risk_tolerance}
                                            onChange={(event) => updateSection("financial", "risk_tolerance", event.target.value)}
                                        >
                                            <option value="conservative">Conservative</option>
                                            <option value="moderate">Moderate</option>
                                            <option value="aggressive">Aggressive</option>
                                        </select>
                                    </label>
                                </div>
                                <label>
                                    <span>Investment time horizon</span>
                                    <select
                                        value={formData.financial.investment_time_horizon}
                                        onChange={(event) => updateSection("financial", "investment_time_horizon", event.target.value)}
                                    >
                                        <option value="short_term">Short term</option>
                                        <option value="medium_term">Medium term</option>
                                        <option value="long_term">Long term</option>
                                    </select>
                                </label>
                                <div className="onboarding-choice-grid">
                                    {[
                                        { label: "Growth", value: "growth" },
                                        { label: "Income", value: "income" },
                                        { label: "Capital preservation", value: "capital_preservation" },
                                    ].map((item) => (
                                        <label key={item.value} className="onboarding-check-card">
                                            <input
                                                type="checkbox"
                                                checked={formData.financial.investment_objective.includes(item.value)}
                                                onChange={() => toggleSelection("financial", "investment_objective", item.value)}
                                            />
                                            <span>{item.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {activeStep === 3 ? (
                            <section className="onboarding-card onboarding-card-compact">
                                <h2>Disclosures</h2>
                                <p>Required regulatory disclosures.</p>
                                <div className="onboarding-choice-grid">
                                    {[
                                        ["is_control_person", "I am a control person"],
                                        ["is_affiliated_exchange_or_finra", "I am affiliated with an exchange or FINRA"],
                                        ["is_politically_exposed", "I am politically exposed"],
                                        ["immediate_family_exposed", "My immediate family is politically exposed"],
                                    ].map(([field, label]) => (
                                        <label key={field} className="onboarding-check-card">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(formData.disclosures[field])}
                                                onChange={(event) => updateSection("disclosures", field, event.target.checked)}
                                            />
                                            <span>{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {activeStep === 4 ? (
                            <section className="onboarding-card onboarding-card-compact">
                                <h2>Agreements</h2>
                                <p>Accept the required brokerage agreements.</p>
                                <label className="onboarding-check-card onboarding-check-card-full">
                                    <input
                                        type="checkbox"
                                        checked={formData.agreements.customer_agreement}
                                        onChange={(event) => updateSection("agreements", "customer_agreement", event.target.checked)}
                                    />
                                    <span>
                                        I agree to the customer agreement and authorize electronic
                                        delivery of required disclosures.
                                    </span>
                                </label>
                                <label>
                                    <span>Signing IP address</span>
                                    <input
                                        value={formData.agreements.ip_address}
                                        onChange={(event) => updateSection("agreements", "ip_address", event.target.value)}
                                    />
                                </label>
                            </section>
                        ) : null}

                        {activeStep === 5 ? (
                            <section className="onboarding-card onboarding-review-card">
                                <h2>Review & Submit</h2>
                                <p>
                                    Review your application details before submission. You can jump
                                    back to any section to make changes.
                                </p>
                                <div className="onboarding-review-sections">
                                    {reviewSections.map((section, index) => (
                                        <article key={section.title} className="onboarding-review-section">
                                            <div className="onboarding-review-header">
                                                <h3>{section.title}</h3>
                                                <button
                                                    type="button"
                                                    onClick={() => setActiveStep(index)}
                                                >
                                                    Edit
                                                </button>
                                            </div>
                                            <div className="onboarding-review-lines">
                                                {section.lines.map((line) => (
                                                    <p key={line}>{line}</p>
                                                ))}
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {errorMessage ? (
                            <div className="onboarding-inline-error">{errorMessage}</div>
                        ) : null}

                        <div className="onboarding-actions">
                            {activeStep > 0 ? (
                                <button
                                    type="button"
                                    className="onboarding-button onboarding-button-secondary"
                                    onClick={() => setActiveStep((prev) => Math.max(prev - 1, 0))}
                                >
                                    Back
                                </button>
                            ) : null}
                            <button
                                type="button"
                                className="onboarding-button onboarding-button-primary"
                                onClick={saveCurrentStep}
                                disabled={isSaving}
                            >
                                {isSaving
                                    ? activeStep === STEP_CONFIG.length - 1
                                        ? "Submitting..."
                                        : "Saving..."
                                    : activeStep === STEP_CONFIG.length - 1
                                        ? "Submit"
                                        : "Save & Continue"}
                            </button>
                        </div>
                    </div>

                    <aside className="onboarding-sidebar">
                        <section className="onboarding-side-card">
                            <h3>Draft Status</h3>
                            <p>Progress is saved after each step.</p>
                            {draft?.status ? (
                                <div className="onboarding-status-pill">
                                    {String(draft.status).replaceAll("_", " ")}
                                </div>
                            ) : null}
                        </section>
                        <section className="onboarding-side-card">
                            <h3>Next</h3>
                            <ul>
                                <li>Finish required sections.</li>
                                <li>Review the saved draft.</li>
                                <li>Submit when complete.</li>
                            </ul>
                        </section>
                    </aside>
                </div>
            </div>
        </section>
    );
}

export default AccountOnboarding;
